import { useState, useEffect, useCallback } from 'react';
import { getTodoItems } from '../../../api/todo';
import { format, subDays, isToday, isYesterday, isWithinInterval } from 'date-fns';

export const useTodoListState = (todolists) => {
  const [todoListCounts, setTodoListCounts] = useState({});
  const [mostRecentLists, setMostRecentLists] = useState([]);
  const [groupedLists, setGroupedLists] = useState({
    today: [],
    yesterday: [],
    previous7Days: [],
    previous30Days: [],
    byMonth: {},
    archived: [],
  });

  // Memoized function to group lists by date
  const groupListsByDate = useCallback((lists) => {
    if (!lists || lists.length === 0) {
      return {
        today: [],
        yesterday: [],
        previous7Days: [],
        previous30Days: [],
        byMonth: {},
        archived: []
      };
    }

    const now = new Date();
    const sevenDaysAgo = subDays(now, 7);
    const thirtyDaysAgo = subDays(now, 30);

    const grouped = {
      today: [],
      yesterday: [],
      previous7Days: [],
      previous30Days: [],
      byMonth: {},
      archived: []
    };

    lists.forEach(list => {
      const listDate = new Date(list.updated_at || list.created_at);

      if (list.archived) {
        grouped.archived.push(list);
        return;
      }

      if (isToday(listDate)) {
        grouped.today.push(list);
      } else if (isYesterday(listDate)) {
        grouped.yesterday.push(list);
      } else if (isWithinInterval(listDate, { start: sevenDaysAgo, end: now })) {
        grouped.previous7Days.push(list);
      } else if (isWithinInterval(listDate, { start: thirtyDaysAgo, end: now })) {
        grouped.previous30Days.push(list);
      } else {
        const monthYear = format(listDate, 'MMMM yyyy');
        if (!grouped.byMonth[monthYear]) {
          grouped.byMonth[monthYear] = [];
        }
        grouped.byMonth[monthYear].push(list);
      }
    });

    // Sort each group by date (newest first)
    const sortByDate = (a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
    grouped.today.sort(sortByDate);
    grouped.yesterday.sort(sortByDate);
    grouped.previous7Days.sort(sortByDate);
    grouped.previous30Days.sort(sortByDate);
    Object.values(grouped.byMonth).forEach(monthLists => monthLists.sort(sortByDate));

    return grouped;
  }, []);

  const fetchTodoListCounts = useCallback(async () => {
    if (!todolists?.length) return;
    
    const counts = await Promise.all(
      todolists.map(async (list) => {
        try {
          const todoItems = await getTodoItems(list.id);
          return {
            id: list.id,
            total: todoItems.length,
            completed: todoItems.filter(item => item.completed).length
          };
        } catch (error) {
          console.error(`Error fetching todo items for list ${list.id}:`, error);
          return { id: list.id, total: 0, completed: 0 };
        }
      })
    );

    setTodoListCounts(
      counts.reduce((acc, {id, total, completed}) => {
        acc[id] = {total, completed};
        return acc;
      }, {})
    );
  }, [todolists]);

  useEffect(() => {
    if (todolists.length > 0) {
      fetchTodoListCounts();
    }
  }, [fetchTodoListCounts]);

  // Effect to update groupedLists when todolists changes
  // This is critical for ensuring database values are prioritized
  useEffect(() => {
    // console.log('useTodoListState: todolists changed, regrouping lists');
    
    // Clear most recent lists to ensure fresh data
    setMostRecentLists([]);
    
    // Create a deep clone of the todolists to ensure React detects changes
    // This is important for breaking reference equality
    const clonedLists = JSON.parse(JSON.stringify(todolists));
    
    // Group the lists by date
    const newGroupedLists = groupListsByDate(clonedLists);
    
    // console.log('useTodoListState: setting new grouped lists from database values');
    setGroupedLists(newGroupedLists);
  }, [todolists, groupListsByDate]);

  // Function to manually update a single list (useful after creation/update)
  const updateSingleList = useCallback((updatedList) => {
    // Force a deep clone to ensure React detects the change
    const clonedList = JSON.parse(JSON.stringify(updatedList));
    
    // console.log('updateSingleList called with:', {
    //   id: clonedList.id,
    //   name: clonedList.list_name,
    //   archived: clonedList.archived,
    //   deleted: clonedList._deleted
    // });
    
    // Handle deleted lists
    if (clonedList._deleted) {
      // console.log('Handling deleted list:', clonedList.id);
      
      // Remove the list from groupedLists
      setGroupedLists(prevGroups => {
        // Create a new array without the deleted list for each group
        const newGroups = { ...prevGroups };
        
        // Remove from today
        newGroups.today = newGroups.today.filter(list => list.id !== clonedList.id);
        
        // Remove from yesterday
        newGroups.yesterday = newGroups.yesterday.filter(list => list.id !== clonedList.id);
        
        // Remove from previous7Days
        newGroups.previous7Days = newGroups.previous7Days.filter(list => list.id !== clonedList.id);
        
        // Remove from previous30Days
        newGroups.previous30Days = newGroups.previous30Days.filter(list => list.id !== clonedList.id);
        
        // Remove from archived
        newGroups.archived = newGroups.archived.filter(list => list.id !== clonedList.id);
        
        // Remove from byMonth
        Object.keys(newGroups.byMonth).forEach(month => {
          newGroups.byMonth[month] = newGroups.byMonth[month].filter(list => list.id !== clonedList.id);
        });
        
        return newGroups;
      });
      
      // Remove from todoListCounts
      setTodoListCounts(prevCounts => {
        const newCounts = { ...prevCounts };
        delete newCounts[clonedList.id];
        return newCounts;
      });
      
      return;
    }
    
    // Update todoListCounts
    setTodoListCounts(prevCounts => ({
      ...prevCounts,
      [clonedList.id]: clonedList.item_count || prevCounts[clonedList.id] || 0
    }));

    // Update groupedLists - IMPORTANT: Don't use todolists from closure, use prevGroups to derive current lists
    setGroupedLists(prevGroups => {
      // console.log('Updating groupedLists for list:', clonedList.id);
      
      // Extract all lists from previous groups to ensure we have the latest state
      const allLists = [
        ...prevGroups.today,
        ...prevGroups.yesterday,
        ...prevGroups.previous7Days,
        ...prevGroups.previous30Days,
        ...prevGroups.archived,
        ...Object.values(prevGroups.byMonth).flat()
      ];
      
      // Remove duplicates (a list might appear in multiple categories during transitions)
      const uniqueLists = Array.from(
        new Map(allLists.map(list => [list.id, list])).values()
      );
      
      // Create a new array with the updated list
      const updatedLists = uniqueLists.map(list => {
        if (list.id === clonedList.id) {
          // console.log('Replacing list in lists:', {
          //   oldName: list.list_name,
          //   newName: clonedList.list_name
          // });
          return clonedList;
        }
        return list;
      });
      
      // If the list doesn't exist in lists, add it
      if (!updatedLists.some(list => list.id === clonedList.id)) {
        // console.log('Adding new list:', clonedList.id);
        updatedLists.push(clonedList);
      }
      
      // Regroup the lists
      const newGroups = groupListsByDate(updatedLists);
      
      // Log the first few lists in each group
      // console.log('New grouped lists:', {
      //   today: newGroups.today.slice(0, 3).map(list => ({
      //     id: list.id,
      //     name: list.list_name
      //   })),
      //   yesterday: newGroups.yesterday.slice(0, 3).map(list => ({
      //     id: list.id,
      //     name: list.list_name
      //   }))
      // });
      
      return newGroups;
    });
    
    // Also directly update any matching list in the todolists array
    // This ensures that future calls to updateSingleList have the latest data
    const listIndex = todolists.findIndex(list => list.id === clonedList.id);
    if (listIndex !== -1) {
      // console.log('Directly updating todolists array at index:', listIndex);
      // We can't modify todolists directly (it's a prop), but this helps with debugging
    }
  }, [groupListsByDate, todolists]);

  return {
    todoListCounts,
    mostRecentLists,
    groupedLists,
    updateSingleList
  };
};
